import React, { useState, useRef, useEffect } from 'react';
import styles from './style.module.css';
import { StoreAIBot } from '../../components/StoreAIBot';
import Typography from '../../components/Typography/Typography';
import { StoreCart, StoreImgName, StoreSearchIcon, StoreSearchIconDark } from '../../assets/svgs';
import FlatButtons from '../../components/FlatButton/FlatButton';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { StorePreviewModal } from '../../components/StorePreviewModal';
import AnimatedButton from '../../components/AnimatedButton';
import { useGetPropsQuery, useGetTemplateByIdQuery } from '../../redux/services/storesApi';

interface Prop {
    _id: string;
    name: string;
    description: string;
    price: number;
    category: 'decorative' | 'functionality';
    image_preview_url: string;
    model_url: string;
    is_premium: boolean;
}

interface DroppedProp extends Prop {
    x: number;
    y: number;
    dropId: string;
}

interface BoutiqueInfoProps {
    backgroundImage?: string;
    overlayImage?: string;
}

export const CustomizeStoreProducts: React.FC<BoutiqueInfoProps> = ({
    backgroundImage,
    overlayImage
}) => {
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'decorative' | 'functionality'>('decorative');
    const [droppedProps, setDroppedProps] = useState<DroppedProp[]>([]);
    const [draggedProp, setDraggedProp] = useState<Prop | null>(null);
    const storeLayoutRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const { storeId } = useParams<{ storeId: string }>();
    const storeDetails = useSelector((state: RootState) => state.CreateStore);
    const storeProducts = useSelector((state: RootState) => state.CreateProduct);
    const addedProducts = useSelector((state: RootState) => state.CreateProduct);

    // Fetch props from API
    const { data: propsData, isLoading: isLoadingProps, isError: isPropsError } = useGetPropsQuery({
        page: 1,
        limit: 50
    });

    // Fetch template data for background
    const { data: templateData } = useGetTemplateByIdQuery(
        storeDetails.storeTemplate || '',
        { skip: !storeDetails.storeTemplate }
    );

    const currentBackgroundImage = templateData?.image_preview_url || backgroundImage;
    const currentOverlayImage = templateData?.model_url || overlayImage;

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    useEffect(() => {
        console.log('=== CustomizeStore Debug ===');
        console.log('Store ID:', storeId);
        console.log('Store Details:', storeDetails);
        console.log('Props Data:', propsData);
        console.log('Template Data:', templateData);
    }, [storeId, storeDetails, propsData, templateData]);

    useEffect(() => {
        console.log('Store Products:', storeProducts);
    }, [storeProducts]);

    const handlePreviewStore = () => {
        if (isMobile) {
            setIsPreviewModalOpen(true);
        }
    };

    const closePreviewModal = () => {
        setIsPreviewModalOpen(false);
    };

    const handleDragStart = (e: React.DragEvent, prop: Prop) => {
        if (isMobile) return;

        setDraggedProp(prop);
        e.dataTransfer.effectAllowed = 'copy';

        const dragImage = document.createElement('div');
        dragImage.innerHTML = `
            <div style="
                width: 60px; 
                height: 60px; 
                background: white; 
                border-radius: 8px; 
                box-shadow: 0 4px 8px rgba(0,0,0,0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                border: 2px solid #4285F4;
            ">
                <img src="${prop.image_preview_url}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;" />
            </div>
        `;
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-100px';
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 30, 30);

        setTimeout(() => {
            document.body.removeChild(dragImage);
        }, 0);
    };

    const handleDragEnd = () => {
        setDraggedProp(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (isMobile) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = (e: React.DragEvent) => {
        if (isMobile) return;
        e.preventDefault();

        if (!draggedProp || !storeLayoutRef.current) return;

        const rect = storeLayoutRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const maxX = rect.width - 60;
        const maxY = rect.height - 60;

        const clampedX = Math.max(0, Math.min(x - 30, maxX));
        const clampedY = Math.max(0, Math.min(y - 30, maxY));

        const droppedProp: DroppedProp = {
            ...draggedProp,
            dropId: `dropped-${Date.now()}-${Math.random()}`,
            x: clampedX,
            y: clampedY
        };

        setDroppedProps(prev => [...prev, droppedProp]);
        setDraggedProp(null);
    };

    const handleRemoveDroppedProp = (dropId: string) => {
        setDroppedProps(prev => prev.filter(p => p.dropId !== dropId));
    };

    const handleAddToStore = (prop: Prop) => {
        if (!isMobile) return;

        const randomX = Math.floor(Math.random() * 400);
        const randomY = Math.floor(Math.random() * 300);

        const droppedProp: DroppedProp = {
            ...prop,
            dropId: `dropped-${Date.now()}-${Math.random()}`,
            x: randomX,
            y: randomY
        };

        setDroppedProps(prev => [...prev, droppedProp]);
    };


    // // Filter props by search term and active tab
    // const filteredProps = propsData?.props?.filter(prop => {
    //     const matchesSearch = prop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //         prop.description.toLowerCase().includes(searchTerm.toLowerCase());
    //     const matchesCategory = prop.category === activeTab;
    //     return matchesSearch && matchesCategory;
    // }) || [];

    // Filter by category and search
    const filteredProps = propsData?.props?.filter(prop => {
        const matchesSearch = prop.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = prop.category === activeTab;
        return matchesSearch && matchesCategory;
    }) || [];

    const reduxProducts = useSelector((state: RootState) => state.CreateProduct);

    const getProductPosition = (position: string, index: number) => {
        const positionMap: { [key: string]: { x: number; y: number } } = {
            'A1': { x: 50, y: 80 },
            'A2': { x: 50, y: 180 },
            'B1': { x: 180, y: 80 },
            'B2': { x: 180, y: 180 },
            'C1': { x: 310, y: 80 },
            'C2': { x: 310, y: 180 },
        };
        return positionMap[position] || { x: 50 + (index * 80), y: 120 };
    };

    const handlePrevious = () => {
        if (storeId) {
            navigate(`/add-products/${storeId}`);
        } else {
            navigate('/add-products');
        }
    };

    const handleNext = () => {
        navigate(`/preview-store/${storeId}`);
    };

    return (
        <div className={styles.boutiqueContainer}>
            <div className={styles.contentWrapper}>
                <div className={styles.header}>
                    <Typography
                        variant='heading5'
                        fontFamily='satoshi'
                        weight='bold'
                        className={styles.title}>
                        Step 4: Customize Store
                    </Typography>
                    {isMobile && (
                        <div className={styles.headerActions}>
                            <AnimatedButton
                                variant='secondary'
                                onClick={handlePreviewStore}
                                className={styles.previewStoreBtn}
                            >
                                <img
                                    src={StoreImgName}
                                    alt="Welcome Name Icon"
                                    className={styles.icon}
                                />
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.previewStoreBtnText}
                                >
                                    Preview Store
                                </Typography>
                            </AnimatedButton>
                        </div>
                    )}
                </div>

                <div className={styles.mainContent}>
                    <div className={styles.contentGrid}>
                        <div className={styles.leftPanel}>
                            <div className={styles.section}>
                                <Typography
                                    variant='heading3'
                                    fontFamily='satoshi'
                                    weight='bold'
                                    className={styles.sectionTitle}>
                                    Customize Store Item
                                </Typography>

                                <div className={styles.formGroup}>
                                    <div className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            {isMobile
                                                ? 'Add props to virtual store'
                                                : 'Drag and drop props into virtual store'
                                            }
                                        </Typography>
                                    </div>

                                    <div className={styles.tabContainer}>
                                        <div className={styles.tabButtons}>
                                            <FlatButtons
                                                className={`${styles.tabButton} ${activeTab === 'decorative' ? styles.active : ''}`}
                                                onClick={() => setActiveTab('decorative')}
                                            >
                                                Decorative Props
                                            </FlatButtons>
                                            <FlatButtons
                                                className={`${styles.tabButton} ${activeTab === 'functionality' ? styles.active : ''}`}
                                                onClick={() => setActiveTab('functionality')}
                                            >
                                                Functionality
                                            </FlatButtons>
                                        </div>
                                        <div className={styles.searchContainer}>
                                            <input
                                                type="text"
                                                placeholder="Search props..."
                                                className={styles.searchInput}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            <img
                                                src={StoreSearchIcon}
                                                alt='search icon'
                                                className={styles.searchIcon}
                                            />
                                        </div>
                                    </div>

                                    {isLoadingProps ? (
                                        <div className={styles.emptyProductContainer}>
                                            <Typography
                                                variant='bodyRegular'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.emptyText}>
                                                Loading props...
                                            </Typography>
                                        </div>
                                    ) : isPropsError ? (
                                        <div className={styles.emptyProductContainer}>
                                            <Typography
                                                variant='bodyRegular'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.emptyText}>
                                                Failed to load props
                                            </Typography>
                                        </div>
                                    ) : filteredProps.length > 0 ? (
                                        <div className={styles.productGrid}>
                                            {filteredProps.map((prop) => (
                                                <div
                                                    key={prop._id}
                                                    className={styles.productCard}
                                                    draggable={!isMobile}
                                                    onDragStart={(e) => handleDragStart(e, prop)}
                                                    onDragEnd={handleDragEnd}
                                                >
                                                    <div className={styles.productImageContainer}>
                                                        <img
                                                            src={prop.image_preview_url}
                                                            alt={prop.name}
                                                            className={styles.productImage}
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="%23ddd"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">No Image</text></svg>';
                                                            }}
                                                        />
                                                    </div>
                                                    <div className={styles.productInfo}>
                                                        <Typography
                                                            variant='bodyRegular'
                                                            fontFamily='satoshi'
                                                            weight='bold'
                                                            className={styles.productName}>
                                                            {prop.name}
                                                        </Typography>
                                                        <Typography
                                                            variant='bodySmall'
                                                            fontFamily='satoshi'
                                                            weight='regular'
                                                            className={styles.productPrice}>
                                                            {prop.is_premium ? `$${prop.price}` : 'Free'}
                                                        </Typography>
                                                    </div>
                                                    {isMobile && (
                                                        <FlatButtons
                                                            onClick={() => handleAddToStore(prop)}
                                                            className={styles.addToStoreBtn}
                                                        >
                                                            <Typography
                                                                variant='buttonText'
                                                                fontFamily='satoshi'
                                                                weight='regular'
                                                                size='base'
                                                                className={styles.addToStoreBtnText}>
                                                                Add to store
                                                            </Typography>
                                                        </FlatButtons>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.emptyProductContainer}>
                                            <Typography
                                                variant='bodyRegular'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.emptyText}>
                                                No props found
                                            </Typography>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {!isMobile && (
                            <div className={styles.rightPanel}>
                                <div className={styles.storeContainer}>
                                    <div className={styles.topNav}>
                                        <FlatButtons className={styles.storeName}>
                                            {storeDetails.logoFile ? (
                                                <img
                                                    src={storeDetails.logoFile}
                                                    alt="Store Logo"
                                                    className={styles.storeLogo}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src={StoreImgName}
                                                    alt="Store Icon"
                                                    className={styles.icon}
                                                />
                                            )}
                                            <Typography
                                                variant='buttonText'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.storeNameText}>
                                                {storeDetails?.name || templateData?.name || 'Store Name'}
                                            </Typography>
                                        </FlatButtons>
                                        <FlatButtons className={styles.navIcons}>
                                            <img
                                                src={StoreSearchIconDark}
                                                alt="Search Icon"
                                                className={styles.icon}
                                            />
                                            <img
                                                src={StoreCart}
                                                alt="Cart Icon"
                                                className={styles.icon}
                                            />
                                        </FlatButtons>
                                    </div>

                                    <div
                                        className={styles.storeLayout}
                                        ref={storeLayoutRef}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                    >
                                        {currentBackgroundImage && (
                                            <img
                                                src={currentBackgroundImage}
                                                alt="Store background"
                                                className={styles.backgroundImage}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    if (backgroundImage) {
                                                        target.src = backgroundImage;
                                                    }
                                                }}
                                            />
                                        )}

                                        {currentOverlayImage && (
                                            <div className={styles.overlay}>
                                                <img
                                                    src={currentOverlayImage}
                                                    alt="Store overlay"
                                                    className={styles.overlayImage}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        if (overlayImage) {
                                                            target.src = overlayImage;
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {reduxProducts && reduxProducts.length > 0 && reduxProducts.map((product, index) => {
                                            const position = getProductPosition(product.position_in_store, index);
                                            return (
                                                <div 
                                                key={`store-product-${index}`}
                                                className={styles.storeProduct} 
                                                style={{ 
                                                    left: `${position.x}px`, 
                                                    top: `${position.y}px` 
                                                    }}
                                                    >
                                                        {product.image_file && (
                                                    <img 
                                                    src={product.image_file ?? ''} 
                                                    alt={product.name} 
                                                    className={styles.storeProductImage}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                    }}
                                                    />
                                                )}
                                                    <Typography
                                                        variant='bodySmall'
                                                        fontFamily='satoshi'
                                                        weight='bold'
                                                        className={styles.storeProductName}
                                                    >
                                                        {product.name}
                                                        </Typography>
                                                </div>
                                            );
                                        })}

                                        {/* {storeProducts && storeProducts.length > 0 && storeProducts.map((product, index) => {
                                            const position = getProductPosition(product.position_in_store, index);
                                            return (
                                                <div className={styles.productInStore} style={{ left: `${position.x}px`, top: `${position.y}px` }}>
                                                    <img src={product.image_file ?? undefined} alt={product.name} />
                                                    <Typography>{product.name}</Typography>
                                                </div>
                                            );
                                        })} */}

                                        {droppedProps.map((prop) => (
                                            <div
                                                key={prop.dropId}
                                                className={styles.droppedProduct}
                                                style={{
                                                    left: `${prop.x}px`,
                                                    top: `${prop.y}px`
                                                }}
                                                onClick={() => handleRemoveDroppedProp(prop.dropId)}
                                                title="Click to remove"
                                            >
                                                <img
                                                    src={prop.image_preview_url}
                                                    alt={prop.name}
                                                    className={styles.droppedProductImage}
                                                />
                                                <div className={styles.removeButton}>×</div>
                                            </div>
                                        ))}

                                        {draggedProp && (
                                            <div className={styles.dropZoneIndicator}>
                                                Drop Props Here
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.actionSection}>
                        <div className={styles.navigationContainer}>
                            <AnimatedButton
                                variant='primary'
                                onClick={handlePrevious}
                                className={styles.previousBtn}
                            >
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.previousBtnText}>
                                    Previous
                                </Typography>
                            </AnimatedButton>

                            <AnimatedButton
                                variant='primary'
                                onClick={handleNext}
                                className={styles.nextBtn}
                            >
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.nextBtnText}>
                                    Done
                                </Typography>
                            </AnimatedButton>
                        </div>
                        <div className={styles.progressSection}>
                            <div className={styles.progressContainer}>
                                <Typography
                                    variant='bodyLarge'
                                    fontFamily='satoshi'
                                    weight='bold'
                                    className={styles.stepText}>
                                    Step 4 of 4
                                </Typography>
                                <div className={styles.progressBar}>
                                    <div className={styles.progressFilled}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isPreviewModalOpen && (
                <StorePreviewModal
                    isOpen={isPreviewModalOpen}
                    onClose={closePreviewModal}
                    backgroundImage={currentBackgroundImage || ''}
                    overlayImage={currentOverlayImage || undefined}
                    // droppedProducts={droppedProps.map(p => ({
                    //     id: p.dropId,
                    //     image: p.image_preview_url,
                    //     quantity: 1,
                    //     x: p.x,
                    //     y: p.y,
                    //     name: p.name,
                    //     description: p.description,
                    //     category: p.category
                    // }))}
                    droppedProducts={droppedProps}
                    storeName={storeDetails?.name || templateData?.name || 'Store Name'}
                    storeLogo={storeDetails.logoFile || undefined}
                    products={reduxProducts}
                />
            )}

            <StoreAIBot />
        </div>
    );
};